import { useEffect } from "react";

import { Thread } from "@/components/organisms/Thread/ThreadPanel";
import { ThreadListLayout } from "@/components/organisms/Thread/ThreadListLayout";
import { ThreadTabBar } from "@/components/organisms/Thread/ThreadTabBar";
import { useDevice } from "@/hooks/useDevice";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { State } from "@/state";

/**
 * How much of the chat panel's width the list takes here. The chat's own
 * width is the one kept: the list opens at this share of it, and sets it back
 * from its own when resized, so the chat reopens at the width this implies.
 */
const LIST_SHARE_OF_CHAT = 0.8;

export function ThreadView() {
  const colors = useLayoutColors();
  const isCollapsed = State((s) => s.isThreadListCollapsed);
  const setIsThreadListCollapsed = State((s) => s.setIsThreadListCollapsed);
  const { isNarrow } = useDevice();
  // On a phone - or in a window as narrow - the list covers the whole chat, so
  // the view opens on the chat and the recent ones are a tap away; a desktop
  // keeps the list as it was left, beside the chat.
  useEffect(() => {
    if (isNarrow) State.getState().setIsThreadListCollapsed(true);
  }, [isNarrow]);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Subnav: thread tabs */}
      <div className="h-12 shrink-0 flex items-center [border-bottom:1px_solid_var(--border)] bg-background pl-2 pr-2 gap-1">
        <div className="flex-1 min-w-0 flex h-full">
          <ThreadTabBar
            isListOpen={!isCollapsed}
            onListToggle={() => setIsThreadListCollapsed(!isCollapsed)}
          />
        </div>
      </div>

      {/* Thread, with the thread list docked on its right */}
      <ThreadListLayout
        id="thread-view"
        // Read, not subscribed to - the list takes its width when it is put
        // up.
        listWidth={
          (State.getState().threadPanelWidth ?? 0) * LIST_SHARE_OF_CHAT || null
        }
        onListResize={(width) =>
          State.getState().setThreadPanelWidth(width / LIST_SHARE_OF_CHAT)
        }
        isOpen={!isCollapsed}
        onOpenChange={(isOpen) => setIsThreadListCollapsed(!isOpen)}
      >
        <Thread showAllThreads={false} stickyBackdropColor={colors.content} />
      </ThreadListLayout>
    </div>
  );
}
